import config from "@/config";
import { getCookieValue } from "@/utilities/getCookieValue";
/** GA4 client ID cookie, present for every visitor who accepted cookies. */
export var GA_CLIENT_ID_COOKIE = "_ga";
/** Written by the Conversion Linker tag, present only after a Google ad click. */
export var GCLID_COOKIE = "_gcl_aw";
/**
 * gtag('get') answers through a callback that never fires when the GA4 config
 * for the measurement ID is not loaded on the page (e.g. blocked by consent),
 * so the accessor is raced against this before falling back to the cookie.
 */
export var GTAG_GET_TIMEOUT_MS = 1000;
/**
 * "GA1.1.971204917.1736259094" -> "971204917.1736259094"
 *
 * The prefix is "GA<version>.<domain depth>." - the depth digit varies, so it
 * is matched rather than hard-coded. Anything not in that shape yields nothing
 * rather than a garbage identifier.
 */
export var parseGaClientId = function (raw) {
    if (!raw)
        return undefined;
    var match = /^GA\d+\.\d+\.(.+)$/.exec(raw.trim());
    return (match === null || match === void 0 ? void 0 : match[1]) || undefined;
};
/**
 * "GCL.1756134022.Cj0KCQjw..." -> "Cj0KCQjw..."
 *
 * Everything after the second dot is the click ID; it is joined back together
 * in case the ID itself ever contains a dot.
 */
export var parseGclid = function (raw) {
    if (!raw)
        return undefined;
    var parts = raw.trim().split(".");
    if (parts.length < 3)
        return undefined;
    return parts.slice(2).join(".") || undefined;
};
// Window carries a string index signature in @types/window.d.ts, so gtag is
// read through a local cast instead of another global augmentation.
var getGtag = function () {
    var candidate = window.gtag;
    return typeof candidate === "function" ? candidate : undefined;
};
/**
 * The GA4 client ID.
 *
 * Prefers gtag('get', measurementId, 'client_id') - the official accessor,
 * which survives any future change to the cookie format - when the Google tag
 * is available on the page, and falls back to parsing the _ga cookie
 * otherwise, or when the accessor stays silent / answers nothing.
 */
export var getGaClientId = function () {
    var fromCookie = function () { return parseGaClientId(getCookieValue(GA_CLIENT_ID_COOKIE)); };
    var gtag = getGtag();
    if (!gtag)
        return Promise.resolve(fromCookie());
    return new Promise(function (resolve) {
        var settled = false;
        var settle = function (value) {
            if (settled)
                return;
            settled = true;
            clearTimeout(timer);
            resolve(value || fromCookie());
        };
        var timer = setTimeout(function () { return settle(undefined); }, GTAG_GET_TIMEOUT_MS);
        try {
            gtag("get", config.ga4MeasurementId, "client_id", function (id) {
                return settle(typeof id === "string" && id ? id : undefined);
            });
        }
        catch (_a) {
            settle(undefined);
        }
    });
};
/** The Google click ID, from the cookie the Conversion Linker persisted. */
export var getGclid = function () {
    return parseGclid(getCookieValue(GCLID_COOKIE));
};
/**
 * The POST /v1/attribution body, or undefined when there is nothing to send.
 *
 * Only identifiers that are actually present are included - never empty
 * strings or placeholders - and with neither present the call must not be
 * made at all (the backend would answer 400).
 */
export var buildAttributionPayload = function (identifiers, consent) {
    var payload = {
        statistics_consent: consent.statistics,
        marketing_consent: consent.marketing
    };
    if (identifiers.gaClientId)
        payload.ga_client_id = identifiers.gaClientId;
    if (identifiers.gclid)
        payload.gclid = identifiers.gclid;
    if (!payload.ga_client_id && !payload.gclid)
        return undefined;
    return payload;
};
//# sourceMappingURL=data:application/json;base64,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